/**
 * QuestionEditor — edit one prescreen draft criterion (Phase A: PrescreenTab
 * review; reused by the Phase C form builder for plain questions).
 *
 * Two authoring modes, chosen by the caller (presence of `criterion.calculation`):
 *  - a normal question: answer type + choices + a stacked eligibility rule;
 *  - a calculation: a derived value (age from a DOB question; BMI from height +
 *    weight) wired to sibling criteria, with a stacked rule on the COMPUTED value.
 *
 * Eligibility is a tree: one or more leaf checks ({op,value/…}) combined by ONE
 * AND/OR toggle. The editor stores a bare leaf when there is a single condition
 * and a group {combinator, conditions[]} when there are several (mirrors the
 * backend whitelist in crmStudyAi.validateCriteria / normalizeRuleNode). An
 * empty rule means "recruiter judgment" (no auto-check). The rule expresses the
 * condition under which the person REMAINS ELIGIBLE.
 */
import { Trash2 } from 'lucide-react';
import { DraftCriterion } from '../../types';
import { CALC_PRESETS } from './formMeta';

export const CRITERION_CATEGORIES = [
  'age', 'demographics', 'diagnosis', 'medication', 'labs', 'procedure',
  'lifestyle', 'logistics', 'consent', 'washout', 'other',
];
// 'height' renders a feet+inches picker (FormRenderer) and stores total inches;
// it reads as a number for eligibility rules and BMI. See the height CHECK in
// migration 20260620010000_crm_prescreen_height_type.sql.
const ANSWER_TYPES = ['boolean', 'number', 'choice', 'text', 'date', 'height'];
// Per-condition operators (the "no auto-check" option is expressed by removing
// every condition, so it isn't a value here).
const CONDITION_OPS = [
  { value: 'eq', label: 'Equals' },
  { value: 'neq', label: 'Does not equal' },
  { value: 'gt', label: 'Greater than' },
  { value: 'gte', label: 'At least' },
  { value: 'lt', label: 'Less than' },
  { value: 'lte', label: 'At most' },
  { value: 'between', label: 'Between (inclusive)' },
  { value: 'in', label: 'One of' },
  { value: 'exists', label: 'Any answer given' },
];

const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const labelCls = 'mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400';

/** One leaf criterion (a single sibling that a calc may reference). */
export interface CriterionSibling {
  ref?: string;
  display_question: string;
  answer_type: string;
  calculation?: DraftCriterion['calculation'];
}

interface QuestionEditorProps {
  criterion: DraftCriterion;
  onChange: (next: DraftCriterion) => void;
  onRemove?: () => void;
  /** The OTHER draft criteria — populates calculation input pickers by `ref`. */
  siblings?: CriterionSibling[];
}

// --- rule <-> editor (combinator + condition list) round-trips -------------
type Combinator = 'and' | 'or';

function ruleToConditions(rule: any): { combinator: Combinator; conditions: any[] } {
  if (rule && rule.combinator && Array.isArray(rule.conditions)) {
    return { combinator: rule.combinator === 'or' ? 'or' : 'and', conditions: rule.conditions };
  }
  if (rule && rule.op) return { combinator: 'and', conditions: [rule] };
  return { combinator: 'and', conditions: [] };
}

function conditionsToRule(combinator: Combinator, conditions: any[]): any {
  const valid = conditions.filter((c) => c && c.op);
  if (valid.length === 0) return {};
  if (valid.length === 1) return valid[0];
  return { combinator, conditions: valid };
}

/** A single leaf condition: operator + its value input(s). */
function ConditionRow({
  condition, answerType, onChange, onRemove,
}: { condition: any; answerType: string; onChange: (c: any) => void; onRemove: () => void }) {
  const op = condition.op || 'eq';
  const valueInput = () => {
    if (op === 'exists') return null;
    if (op === 'between') {
      return (
        <div className="flex items-center gap-2">
          <input
            type="number" placeholder="Min" value={condition.min ?? ''} className={inputCls}
            onChange={(e) => onChange({ ...condition, min: e.target.value === '' ? undefined : Number(e.target.value) })}
          />
          <span className="text-xs text-gray-400">to</span>
          <input
            type="number" placeholder="Max" value={condition.max ?? ''} className={inputCls}
            onChange={(e) => onChange({ ...condition, max: e.target.value === '' ? undefined : Number(e.target.value) })}
          />
        </div>
      );
    }
    if (op === 'in') {
      return (
        <input
          type="text"
          placeholder="Comma-separated passing values"
          value={Array.isArray(condition.values) ? condition.values.join(', ') : ''}
          className={inputCls}
          onChange={(e) => onChange({ ...condition, values: e.target.value.split(',').map((v) => v.trim()).filter(Boolean) })}
        />
      );
    }
    if (answerType === 'boolean' && (op === 'eq' || op === 'neq')) {
      return (
        <select
          value={condition.value === true ? 'true' : condition.value === false ? 'false' : ''}
          className={inputCls}
          onChange={(e) => onChange({ ...condition, value: e.target.value === 'true' })}
        >
          <option value="" disabled>Pick…</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }
    // height stores total inches — compare it numerically like a number.
    const numeric = ['gt', 'gte', 'lt', 'lte'].includes(op) || answerType === 'number' || answerType === 'height';
    return (
      <input
        type={numeric ? 'number' : 'text'}
        placeholder="Passing value"
        value={condition.value ?? ''}
        className={inputCls}
        onChange={(e) => onChange({
          ...condition,
          value: numeric ? (e.target.value === '' ? undefined : Number(e.target.value)) : e.target.value,
        })}
      />
    );
  };
  return (
    <div className="flex flex-wrap items-start gap-2">
      {/* Changing the operator clears stale value/min/max/values. */}
      <select value={op} className={`${inputCls} w-40`} onChange={(e) => onChange({ op: e.target.value })}>
        {CONDITION_OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <div className="min-w-[10rem] flex-1">{valueInput()}</div>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove condition"
        className="rounded p-1.5 text-gray-400 transition-colors duration-150 hover:text-red-600"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default function QuestionEditor({ criterion: c, onChange, onRemove, siblings = [] }: QuestionEditorProps) {
  const rule = c.rule && typeof c.rule === 'object' ? c.rule : {};
  const setRule = (next: any) => onChange({ ...c, rule: next });
  const set = (patch: Partial<DraftCriterion>) => onChange({ ...c, ...patch });
  const isCalc = !!c.calculation;
  // The eligibility rule evaluates the COMPUTED value for a calc (always numeric).
  const ruleAnswerType = isCalc ? 'number' : c.answer_type;
  const { combinator, conditions } = ruleToConditions(rule);
  const setConditions = (combo: Combinator, conds: any[]) => setRule(conditionsToRule(combo, conds));
  const defaultOp = ruleAnswerType === 'number' || ruleAnswerType === 'height' ? 'gte' : 'eq';

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/60">
      <div className="flex flex-wrap items-start gap-3">
        <div className="w-full sm:w-32">
          <label className={labelCls}>Kind</label>
          <select value={c.criterion_kind} className={inputCls} onChange={(e) => set({ criterion_kind: e.target.value })}>
            <option value="inclusion">Inclusion</option>
            <option value="exclusion">Exclusion</option>
          </select>
        </div>
        <div className="w-full sm:w-36">
          <label className={labelCls}>Category</label>
          <select value={c.category} className={inputCls} onChange={(e) => set({ category: e.target.value })}>
            {CRITERION_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>{cat.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
        <div className="w-full sm:w-36">
          <label className={labelCls}>Answer type</label>
          {/* The 'calculated value' option converts a normal question into a
              derived value (age/BMI) and back — so a plain-number BMI the AI
              produced can be turned into an auto-computed criterion in place. The
              eligibility rule carries over only when BOTH sides are numeric (a
              BMI range still applies to the computed value); a boolean/choice
              rule would be nonsense on a number, so it's cleared. */}
          <select
            value={isCalc ? 'calculation' : c.answer_type}
            className={inputCls}
            onChange={(e) => {
              const t = e.target.value;
              const wasNumeric = c.answer_type === 'number' || c.answer_type === 'height';
              if (t === 'calculation') {
                // Default the preset from the label (BMI is the common case).
                const preset = /\bbmi\b|body[\s-]*mass[\s-]*index/i.test(c.display_question) ? 'bmi' : 'age';
                set({
                  calculation: { preset, inputs: {} }, answer_type: 'number', options: null, profile_key_suggestion: null,
                  ...(wasNumeric ? {} : { rule: {} }),
                });
              } else {
                // Back to a normal question — drop the calculation wiring; keep
                // the (numeric) rule only when the new plain type is numeric too.
                const toNumeric = t === 'number' || t === 'height';
                set({
                  calculation: null,
                  answer_type: t,
                  options: t === 'choice' ? (c.options?.length ? c.options : [{ value: 'option-1', label: 'Option 1' }, { value: 'option-2', label: 'Option 2' }]) : null,
                  ...(toNumeric ? {} : { rule: {} }),
                });
              }
            }}
          >
            {ANSWER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            <option value="calculation">calculated value</option>
          </select>
        </div>
        {isCalc && (
          <div className="w-full sm:w-40">
            <label className={labelCls}>Calculation</label>
            <select
              value={c.calculation!.preset}
              className={inputCls}
              onChange={(e) => set({ calculation: { preset: e.target.value, inputs: {} }, answer_type: 'number', options: null, profile_key_suggestion: null })}
            >
              {Object.entries(CALC_PRESETS).map(([key, meta]) => (
                <option key={key} value={key}>{meta.label}</option>
              ))}
            </select>
          </div>
        )}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove question"
            className="ml-auto rounded-lg p-2 text-gray-400 transition-colors duration-150 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      <div>
        <label className={labelCls}>{isCalc ? 'Label (shown to the recruiter)' : 'Question (asked verbatim on the phone)'}</label>
        <textarea rows={2} value={c.display_question} className={inputCls} onChange={(e) => set({ display_question: e.target.value })} />
      </div>

      <div>
        <label className={labelCls}>Protocol wording (source)</label>
        <textarea rows={2} value={c.source_text} className={`${inputCls} text-xs text-gray-600 dark:text-gray-300`} onChange={(e) => set({ source_text: e.target.value })} />
      </div>

      {/* Calculation input wiring — one sibling picker per preset slot. */}
      {isCalc && (
        <div className="rounded-lg border border-primary-200 bg-primary-50/40 p-2.5 dark:border-primary-800/60 dark:bg-primary-900/10">
          <label className={labelCls}>Computed from</label>
          <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">{CALC_PRESETS[c.calculation!.preset]?.description}</p>
          <div className="space-y-2">
            {(CALC_PRESETS[c.calculation!.preset]?.inputs || []).map((slot) => {
              const choices = siblings.filter((s) => s.ref && !s.calculation && slot.accept.includes(s.answer_type));
              const current = c.calculation!.inputs?.[slot.name] || '';
              return (
                <div key={slot.name} className="flex flex-wrap items-center gap-2">
                  <span className="w-44 text-xs text-gray-600 dark:text-gray-300">{slot.label}</span>
                  <select
                    value={current}
                    className={`${inputCls} flex-1`}
                    onChange={(e) => set({ calculation: { preset: c.calculation!.preset, inputs: { ...(c.calculation!.inputs || {}), [slot.name]: e.target.value || undefined } as Record<string, string> } })}
                  >
                    <option value="">— pick a question —</option>
                    {choices.map((s) => (
                      <option key={s.ref} value={s.ref}>{s.display_question || '(untitled)'}</option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
          {!siblings.some((s) => s.ref && !s.calculation) && (
            <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">Add the input question(s) above first, then wire them here.</p>
          )}
        </div>
      )}

      {!isCalc && c.answer_type === 'choice' && (
        <div>
          <label className={labelCls}>Choices</label>
          <div className="space-y-1.5">
            {(c.options || []).map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text" value={o.label} className={inputCls}
                  onChange={(e) => {
                    const options = [...(c.options || [])];
                    options[i] = { value: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-') || `option-${i + 1}`, label: e.target.value };
                    set({ options });
                  }}
                />
                <button
                  type="button"
                  aria-label="Remove choice"
                  onClick={() => set({ options: (c.options || []).filter((_, j) => j !== i) })}
                  className="rounded p-1.5 text-gray-400 hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => set({ options: [...(c.options || []), { value: `option-${(c.options?.length || 0) + 1}`, label: '' }] })}
              className="text-xs font-medium text-primary-700 hover:text-primary-800 dark:text-primary-400"
            >
              + Add choice
            </button>
          </div>
        </div>
      )}

      {/* Stacked eligibility rule — N conditions joined by one AND/OR toggle. */}
      <div>
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
            {isCalc ? 'Eligible when (the computed value)' : 'Eligible when (auto-check)'}
          </label>
          {conditions.length >= 2 && (
            <div className="inline-flex overflow-hidden rounded-lg border border-gray-300 text-xs dark:border-gray-600" role="group" aria-label="Combine conditions">
              {(['and', 'or'] as Combinator[]).map((cmb) => (
                <button
                  key={cmb}
                  type="button"
                  onClick={() => setConditions(cmb, conditions)}
                  aria-pressed={combinator === cmb}
                  className={`px-2.5 py-1 font-medium transition-colors duration-150 ${
                    combinator === cmb ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  {cmb === 'and' ? 'Match ALL' : 'Match ANY'}
                </button>
              ))}
            </div>
          )}
        </div>
        {conditions.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-gray-500">No auto-check — recruiter judgment.</p>
        ) : (
          <div className="space-y-2">
            {conditions.map((cond, idx) => (
              <ConditionRow
                key={idx}
                condition={cond}
                answerType={ruleAnswerType}
                onChange={(next) => setConditions(combinator, conditions.map((x, j) => (j === idx ? next : x)))}
                onRemove={() => setConditions(combinator, conditions.filter((_, j) => j !== idx))}
              />
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={() => setConditions(combinator, [...conditions, { op: defaultOp }])}
          className="mt-2 text-xs font-medium text-primary-700 hover:text-primary-800 dark:text-primary-400"
        >
          + Add condition
        </button>
      </div>

      {!isCalc && (
        <div className="w-full sm:w-64">
          <label className={labelCls}>Profile key (cross-study)</label>
          <input
            type="text"
            placeholder="e.g. condition.t2dm"
            value={c.profile_key_suggestion || ''}
            className={inputCls}
            onChange={(e) => set({ profile_key_suggestion: e.target.value.trim().toLowerCase() || null })}
          />
        </div>
      )}
    </div>
  );
}
